import React, { Component } from 'react'
import { reduxForm, Field } from 'redux-form'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import Row from '../../../common/layout/row'
import LabelAndInput from '../../../common/form/LabelAndInput'
import If from '../../../common/operator/If'
import Table from '../../../common/layout/Table'
import Content from '../../../common/template/Content'
import { formatUtcAsLocal } from '../utils/datetime'
import {
    init, create, update, remove, getList, getBotUsers,
    showCreate, showUpdate, toggleEnabled, toggleDryRun, assignUsers, rotateKey
} from '../duck/botOrchestrator.duck'
import CheckMultiSelect from './CheckMultiSelect'

/** Cores por estágio reportado no heartbeat. */
const STATUS_STYLE = {
    offline: { color: '#757575', label: 'Offline' },
    idle: { color: '#1565c0', label: 'Ocioso' },
    scheduled: { color: '#00838f', label: 'Agendado' },
    opening: { color: '#ef6c00', label: 'Abrindo Blaze' },
    logging_in: { color: '#ef6c00', label: 'Logando' },
    watching: { color: '#6a1b9a', label: 'Assistindo' },
    betting: { color: '#2e7d32', label: 'Apostando' },
    login_cooldown: { color: '#c62828', label: 'Login em pausa' },
    login_blocked: { color: '#c62828', label: 'Login bloqueado' },
    error: { color: '#c62828', label: 'Erro' },
}

const BoolSelect = ({ input, label, trueLabel, falseLabel }) => (
    <div className='form-group' style={{ padding: '0 15px' }}>
        <label style={{ fontWeight: 600 }}>{label}</label>
        <select className='form-control' {...input} value={input.value === '' || input.value == null ? 1 : input.value}>
            <option value={1}>{trueLabel}</option>
            <option value={0}>{falseLabel}</option>
        </select>
    </div>
)

class BotOrchestrators extends Component {
    refreshTimer = null

    componentWillMount() {
        if (this.props.userId != null) {
            this.props.getList()
            this.props.getBotUsers()
        }
    }

    componentDidMount() {
        this.refreshTimer = setInterval(() => this.props.getList(), 15000)
    }

    componentDidUpdate(prevProps) {
        if (this.props.userId != null && prevProps.userId == null) {
            this.props.getList()
            this.props.getBotUsers()
        }
    }

    componentWillUnmount() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer)
            this.refreshTimer = null
        }
    }

    onSubmit = (values) => {
        if (values.id) {
            this.props.update(values)
        } else {
            this.props.create(values)
        }
    }

    renderStatus = (status) => {
        const style = STATUS_STYLE[status] || { color: '#757575', label: status || '—' }
        return <b style={{ color: style.color }}>{style.label}</b>
    }

    /**
     * Ids já ativos em qualquer orquestrador, exceto o da linha atual (esses
     * continuam selecionáveis para remover/manter).
     */
    takenUserIds = (exceptOrchestratorId = null) => {
        const taken = new Set()
        ;(this.props.list || []).forEach((row) => {
            if (exceptOrchestratorId != null && Number(row.id) === Number(exceptOrchestratorId)) return
            const ids = Array.isArray(row.user_ids) ? row.user_ids : (row.user_id ? [row.user_id] : [])
            ids.forEach((id) => taken.add(Number(id)))
        })
        return taken
    }

    /** Bots livres + os já atribuídos a esta máquina. */
    optionsFor = (orchestrator) => {
        const taken = this.takenUserIds(orchestrator ? orchestrator.id : null)
        const current = new Set(
            orchestrator && Array.isArray(orchestrator.user_ids)
                ? orchestrator.user_ids.map(Number)
                : []
        )
        return (this.props.botUsers || []).filter(
            (user) => current.has(Number(user.id)) || !taken.has(Number(user.id))
        )
    }

    /**
     * Multi-seleção de bots na linha: checkbox + chips. Só entram usuários
     * livres (ou os desta máquina). Um processo por bot na máquina usa o
     * mesmo name/key + BOT_USER_ID.
     */
    renderUserSelect = (orchestrator) => {
        const options = this.optionsFor(orchestrator)
        const selected = Array.isArray(orchestrator.user_ids)
            ? orchestrator.user_ids.map(Number)
            : []

        return (
            <div style={{ minWidth: 220, maxWidth: 320 }}>
                <CheckMultiSelect
                    options={options}
                    value={selected}
                    onChange={(ids) => this.props.assignUsers(orchestrator, ids)}
                    emptyLabel='Nenhum bot livre'
                    bare
                />
            </div>
        )
    }

    renderState = (state) => {
        if (!state || !Object.keys(state).length) return '—'

        // Heartbeat multi-bot: state.by_user[userId] = { status, state, at }
        if (state.by_user && typeof state.by_user === 'object') {
            const parts = Object.keys(state.by_user).map((uid) => {
                const entry = state.by_user[uid] || {}
                const inner = entry.state || {}
                const bits = [`#${uid}`]
                if (entry.status) bits.push(entry.status)
                if (inner.signal_at) bits.push(String(inner.signal_at).substring(11, 16))
                return bits.join(' ')
            })
            return <span title={JSON.stringify(state.by_user)}>{parts.join(' · ') || '—'}</span>
        }

        const parts = []
        if (state.signal_at) parts.push(`sinal ${String(state.signal_at).substring(11, 16)}`)
        if (state.credit != null) parts.push(`R$ ${Number(state.credit).toFixed(2)}`)
        if (state.bet != null) parts.push(`entrada R$ ${Number(state.bet).toFixed(2)}`)
        if (state.factor != null) parts.push(`${Number(state.factor).toFixed(2)}x`)
        if (state.gale != null) parts.push(`gale ${state.gale}`)

        const summary = parts.length ? parts.join(' · ') : '—'
        return <span title={state.message || summary}>{summary}</span>
    }

    render() {
        return (
            <>
                <If test={this.props.show === 'form'}>
                    <Content>
                        <form onSubmit={this.props.handleSubmit(this.onSubmit)}>
                            <Row>
                                <Field name='name' component={LabelAndInput} readOnly={false} forceToShow
                                    label='Máquina' cols='12 3' placeholder='Ex.: vps-01' error={this.props.errors}
                                    title='Mesmo valor de BOT_ORCHESTRATOR_NAME no .env da máquina.' />
                                <Field name='description' component={LabelAndInput} readOnly={false} forceToShow
                                    label='Descrição' cols='12 5' placeholder='Onde a máquina está hospedada' error={this.props.errors} />
                                <div className='col-xs-12 col-sm-2'>
                                    <Field name='enabled' component={BoolSelect} label='Ativo'
                                        trueLabel='Sim' falseLabel='Pausado' />
                                </div>
                                <div className='col-xs-12 col-sm-2'>
                                    <Field name='dry_run' component={BoolSelect} label='Modo'
                                        trueLabel='Ensaio' falseLabel='Aposta real' />
                                </div>
                            </Row>
                            <If test={!!this.props.accessKey}>
                                <Row>
                                    <div className='col-xs-12 col-sm-8'>
                                        <div className='form-group'>
                                            <label style={{ fontWeight: 600 }}>Chave de acesso</label>
                                            <input className='form-control' readOnly value={this.props.accessKey || ''} />
                                            <small style={{ color: '#757575' }}>
                                                Vai em BOT_ORCHESTRATOR_KEY. Cada bot na máquina usa o mesmo par + BOT_USER_ID.
                                            </small>
                                        </div>
                                    </div>
                                </Row>
                            </If>
                            <Row>
                                <button type='submit' className='btn btn-primary'>Salvar</button>
                                <button type='button' className='btn btn-default' onClick={this.props.init}>Cancelar</button>
                            </Row>
                        </form>
                    </Content>
                </If>
                <If test={this.props.show === 'list'}>
                    <button className='btn btn-primary' onClick={this.props.showCreate}>Adicionar máquina</button>
                    <br /><br />
                    <p style={{ color: '#616161', marginBottom: 12 }}>
                        Uma máquina pode rodar vários bots (marque na lista). Só entram usuários do perfil Bot
                        que ainda não estão ativos em outro orquestrador.
                    </p>
                </If>
                <Table
                    body={[...this.props.list]}
                    labelMobile='name'
                    actions={{
                        update: this.props.showUpdate,
                        remove: this.props.remove,
                        rotate: {
                            title: 'Gerar nova chave',
                            icon: 'fa fa-key',
                            onClick: (row) => this.props.rotateKey(row),
                        },
                    }}
                    attributes={{
                        name: {
                            title: 'Máquina',
                            callback: (val, row) => (
                                <span title={[row.hostname, row.version].filter(Boolean).join(' · ')}>
                                    {val}
                                    {row.description ? <small style={{ display: 'block', color: '#757575' }}>{row.description}</small> : null}
                                </span>
                            ),
                        },
                        resolved_status: {
                            title: 'Status',
                            callback: (val) => this.renderStatus(val),
                        },
                        user_ids: {
                            title: 'Bots',
                            notSort: true,
                            callback: (val, row) => this.renderUserSelect(row),
                        },
                        dry_run: {
                            title: 'Modo',
                            callback: (val, row) => (
                                <button
                                    type='button'
                                    className={`btn btn-xs ${Number(val) === 1 ? 'btn-default' : 'btn-success'}`}
                                    onClick={() => this.props.toggleDryRun(row)}
                                    title='Ensaio percorre todo o fluxo sem confirmar a aposta na Blaze.'
                                >
                                    {Number(val) === 1 ? 'Ensaio' : 'Aposta real'}
                                </button>
                            ),
                        },
                        enabled: {
                            title: 'Ativo',
                            callback: (val, row) => (
                                <button
                                    type='button'
                                    className={`btn btn-xs ${Number(val) === 1 ? 'btn-success' : 'btn-default'}`}
                                    onClick={() => this.props.toggleEnabled(row)}
                                >
                                    {Number(val) === 1 ? 'Sim' : 'Pausado'}
                                </button>
                            ),
                        },
                        state: {
                            title: 'Último estado',
                            notSort: true,
                            callback: (val) => this.renderState(val),
                        },
                        last_seen_at: {
                            title: 'Visto em',
                            callback: (val) => (val ? formatUtcAsLocal(val) : 'nunca'),
                        },
                    }}
                />
            </>
        )
    }
}

BotOrchestrators = reduxForm({ form: 'botOrchestratorForm', destroyOnUnmount: false })(BotOrchestrators)
const mapStateToProps = state => ({
    list: state.botOrchestrator ? state.botOrchestrator.list : [],
    botUsers: state.botOrchestrator ? state.botOrchestrator.botUsers : [],
    show: state.botOrchestrator ? state.botOrchestrator.show : 'list',
    errors: state.botOrchestrator ? state.botOrchestrator.errors : {},
    accessKey: (state.form.botOrchestratorForm && state.form.botOrchestratorForm.values)
        ? state.form.botOrchestratorForm.values.access_key : null,
    userId: state.auth.user ? state.auth.user.id : null,
})
const mapDispatchToProps = dispatch => bindActionCreators({
    init, create, update, remove, getList, getBotUsers,
    showCreate, showUpdate, toggleEnabled, toggleDryRun, assignUsers, rotateKey
}, dispatch)
export default connect(mapStateToProps, mapDispatchToProps)(BotOrchestrators)
